import { inject, Injectable, NgZone } from '@angular/core';
import { Environment, ENVIRONMENT } from '@ld.consumer-portal/models';
import {
  AnalyticsService,
  DynatraceService,
  GlanceCobrowse,
  LoggingService,
  PlatformService,
} from '@ld.consumer-portal/utils';

export interface ViewPdfOptions {
  url: string;
}

/** Stable, translatable reasons a co-browsing session could not be started. */
export type ShareScreenErrorCode = 'localhostUnsupported' | 'sdkUnavailable' | 'sessionAlreadyActive' | 'startFailed';

export interface ShareScreenSessionResult {
  success: boolean;
  error?: string;
  errorCode?: ShareScreenErrorCode;
  sessionCode?: string;
}

interface GlanceCobrowseVisitor {
  startSession: (groupId?: string) => void;
  inSession: () => boolean;
  viewPDF?: (options: ViewPdfOptions) => void;
}

interface GlanceCobrowseNamespace {
  Visitor?: GlanceCobrowseVisitor;
}

interface GlanceSDK {
  Cobrowse?: GlanceCobrowseNamespace;
}

declare global {
  interface Window {
    GLANCE?: GlanceSDK;
  }
}

@Injectable({
  providedIn: 'root',
})
export class GlanceService {
  private static readonly SDK_POLL_INTERVAL_MS = 250;
  private static readonly SDK_POLL_TIMEOUT_MS = 10000;

  private readonly ngZone = inject(NgZone);
  private readonly analyticsService = inject(AnalyticsService);
  private readonly dynatraceService = inject(DynatraceService);
  private readonly loggingService = inject(LoggingService);
  private readonly platformService = inject(PlatformService);
  private readonly env = inject<Environment>(ENVIRONMENT);

  private readonly glanceScriptUrl = `https://cdn.glance.net/cobrowse/CobrowseJS.ashx?group=${this.env.appSettings.glance?.groupId ?? ''}&site=${this.env.appSettings.glance?.site ?? ''}`;
  private readonly glanceGroupId = this.env.appSettings.glance?.groupId ?? '';
  private readonly glanceSite = this.env.appSettings.glance?.site ?? '';
  private readonly glanceWs = this.env.appSettings.glance?.ws ?? '';

  private scriptLoadPromise: Promise<void> | null = null;

  /** Tracks whether the native Glance SDK has been initialized on mobile. */
  private nativeInitialized = false;
  /** Tracks whether a native session is active (updated via plugin event listeners). */
  private nativeInSession = false;
  /** Whether native event listeners have been registered. */
  private nativeListenersRegistered = false;
  /** Guards against concurrent startNativeSession calls. */
  private nativeStartPending = false;
  /** Guards against concurrent ensureNativeLoaded calls (mirrors scriptLoadPromise pattern). */
  private nativeInitPromise: Promise<boolean> | null = null;
  /** Last error from native SDK initialization (for diagnostics). */
  private lastNativeInitError = '';

  isLoaded(): boolean {
    if (this.platformService.isNativeMobile()) {
      return this.nativeInitialized;
    }
    return typeof window.GLANCE !== 'undefined' && typeof window.GLANCE.Cobrowse?.Visitor !== 'undefined';
  }

  private getVisitor(): GlanceCobrowseVisitor | undefined {
    return window.GLANCE?.Cobrowse?.Visitor;
  }

  isInSession(): boolean {
    if (this.platformService.isNativeMobile()) {
      return this.nativeInSession;
    }
    const visitor = this.getVisitor();
    if (!visitor) {
      return false;
    }
    return visitor.inSession();
  }

  private isLocalhost(): boolean {
    // Native mobile production apps use localhost for Capacitor's local server, but should be allowed
    if (this.platformService.isNativeMobile()) {
      return false;
    }
    const hostname = window.location.hostname;
    return hostname === 'localhost' || hostname === '127.0.0.1' || hostname.startsWith('192.168.');
  }

  startSession(): ShareScreenSessionResult {
    if (this.platformService.isNativeMobile()) {
      return { success: false, error: 'Use startSessionAsync() for native mobile.', errorCode: 'startFailed' };
    }

    if (this.isLocalhost()) {
      this.loggingService.logWarning(
        'GLANCE Cobrowse is not available on localhost. This feature only works in deployed environments.',
      );
      return {
        success: false,
        error: 'Screen sharing is only available in deployed environments (not localhost).',
        errorCode: 'localhostUnsupported',
      };
    }

    if (!this.isLoaded()) {
      this.loggingService.logWarning('GLANCE Cobrowse SDK is not loaded');
      return {
        success: false,
        error: 'Screen sharing is not available. Please refresh the page and try again.',
        errorCode: 'sdkUnavailable',
      };
    }

    const visitor = this.getVisitor();
    if (!visitor) {
      this.loggingService.logWarning('GLANCE Cobrowse Visitor is not available');
      return {
        success: false,
        error: 'Screen sharing is not available. Please refresh the page and try again.',
        errorCode: 'sdkUnavailable',
      };
    }

    if (this.isInSession()) {
      this.loggingService.logWarning('Already in a GLANCE co-browsing session');
      return {
        success: false,
        error: 'A screen sharing session is already active.',
        errorCode: 'sessionAlreadyActive',
      };
    }

    try {
      this.ngZone.run(() => {
        visitor.startSession();
        this.analyticsService.trackCoBrowseRequested();
        this.dynatraceService.trackButtonClick('Share My Screen', 'co-browse');
      });
      return { success: true };
    } catch (err) {
      this.loggingService.logException(err, 'Failed to start GLANCE session:');
      return { success: false, error: 'Failed to start screen sharing. Please try again.', errorCode: 'startFailed' };
    }
  }

  /**
   * Loads the web Cobrowse SDK during app startup. The SDK rejoins an in-progress
   * session from its own cookie when it initializes, so it has to be on the page
   * after a full document load (for example the post-login redirect) and not only
   * after the visitor clicks "Share My Screen".
   */
  initializeWebSdk(): void {
    if (this.platformService.isNativeMobile() || this.isLocalhost() || this.isLoaded()) {
      return;
    }

    if (!this.glanceGroupId) {
      return;
    }

    this.loadScript().catch(() =>
      this.loggingService.logWarning('Failed to load the GLANCE cobrowse script during startup'),
    );
  }

  async ensureLoaded(): Promise<boolean> {
    if (this.platformService.isNativeMobile()) {
      return this.ensureNativeLoaded();
    }

    if (this.isLoaded()) {
      return true;
    }

    const readyAfterWait = await this.waitForSdkReady(GlanceService.SDK_POLL_TIMEOUT_MS);
    if (readyAfterWait) {
      return true;
    }

    this.loggingService.logWarning('GLANCE SDK not ready after initial wait, attempting dynamic script reload');
    try {
      await this.loadScript();
    } catch {
      this.loggingService.logWarning('Failed to dynamically load GLANCE script');
      return false;
    }

    return this.waitForSdkReady(GlanceService.SDK_POLL_TIMEOUT_MS);
  }

  async startSessionAsync(): Promise<ShareScreenSessionResult> {
    if (this.platformService.isNativeMobile()) {
      return this.startNativeSession();
    }

    if (this.isLocalhost()) {
      this.loggingService.logWarning(
        'GLANCE Cobrowse is not available on localhost. This feature only works in deployed environments.',
      );
      return {
        success: false,
        error: 'Screen sharing is only available in deployed environments (not localhost).',
        errorCode: 'localhostUnsupported',
      };
    }

    const loaded = await this.ensureLoaded();
    if (!loaded) {
      this.loggingService.logWarning('GLANCE Cobrowse SDK could not be loaded');
      return {
        success: false,
        error: 'Screen sharing is not available. Please refresh the page and try again.',
        errorCode: 'sdkUnavailable',
      };
    }

    return this.startSession();
  }

  canViewPdf(): boolean {
    if (this.platformService.isNativeMobile()) {
      return false;
    }
    const visitor = this.getVisitor();
    return !!visitor && typeof visitor.viewPDF === 'function';
  }

  viewPdf(url: string): { success: boolean; error?: string } {
    if (this.platformService.isNativeMobile()) {
      return { success: false, error: 'PDF sharing via Glance is not supported on native mobile.' };
    }

    if (this.isLocalhost()) {
      this.loggingService.logWarning('GLANCE Cobrowse viewPDF is not available on localhost.');
      return { success: false, error: 'PDF sharing is only available in deployed environments.' };
    }

    if (!this.isLoaded()) {
      this.loggingService.logWarning('GLANCE Cobrowse SDK is not loaded');
      return { success: false, error: 'PDF sharing is not available. Please refresh the page and try again.' };
    }

    if (!this.isInSession()) {
      this.loggingService.logWarning('Not in a GLANCE co-browsing session');
      return { success: false, error: 'No active screen sharing session. Please start a session first.' };
    }

    const visitor = this.getVisitor();
    if (!visitor?.viewPDF) {
      this.loggingService.logWarning('GLANCE Cobrowse viewPDF is not available');
      return { success: false, error: 'PDF sharing is not supported in this session.' };
    }

    try {
      const viewPdfFn = visitor.viewPDF;
      if (viewPdfFn) {
        this.ngZone.run(() => {
          viewPdfFn({ url });
          this.analyticsService.trackPdfCoBrowse(url);
        });
      }
      return { success: true };
    } catch (err) {
      this.loggingService.logException(err, 'Failed to share PDF via GLANCE:');
      return { success: false, error: 'Failed to share PDF. Please try again.' };
    }
  }

  // ---------------------------------------------------------------------------
  // Native mobile SDK (iOS/Android) via Capacitor plugin
  // ---------------------------------------------------------------------------

  private async ensureNativeLoaded(): Promise<boolean> {
    if (this.nativeInitialized) {
      return true;
    }

    if (this.nativeInitPromise) {
      return this.nativeInitPromise;
    }

    const groupIdStr = this.glanceGroupId;
    const groupId = parseInt(groupIdStr, 10);
    if (!groupId || isNaN(groupId)) {
      this.loggingService.logWarning('GLANCE groupId is not configured for native mobile');
      return false;
    }

    this.loggingService.logInfo(`GLANCE native SDK init starting: groupId=${groupId}, site=${this.glanceSite}`);

    this.nativeInitPromise = (async () => {
      try {
        const deviceDetails = await this.platformService.ensureDeviceDetails();
        const visitorId = deviceDetails?.deviceId || `mobile-${Date.now()}`;
        this.loggingService.logInfo(`GLANCE native SDK visitorId=${visitorId}`);

        await GlanceCobrowse.initVisitor({
          groupId,
          visitorId,
          site: this.glanceSite || undefined,
        });
        this.nativeInitialized = true;
        this.registerNativeListeners();
        this.loggingService.logInfo('GLANCE native SDK initialized successfully');
        return true;
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : String(err);
        this.lastNativeInitError = errorMsg;
        this.loggingService.logException(err, `Failed to initialize GLANCE native SDK: ${errorMsg}`);
        return false;
      } finally {
        this.nativeInitPromise = null;
      }
    })();

    return this.nativeInitPromise;
  }

  private async startNativeSession(): Promise<ShareScreenSessionResult> {
    const loaded = await this.ensureNativeLoaded();
    if (!loaded) {
      const detail = this.lastNativeInitError ? ` (${this.lastNativeInitError})` : '';
      return {
        success: false,
        error: `Screen sharing is not available. Native SDK failed to initialize${detail}.`,
        errorCode: 'sdkUnavailable',
      };
    }

    if (this.nativeStartPending) {
      this.loggingService.logWarning('Already in a native GLANCE co-browsing session');
      return {
        success: false,
        error: 'A screen sharing session is already active.',
        errorCode: 'sessionAlreadyActive',
      };
    }

    this.nativeStartPending = true;
    try {
      const status = await GlanceCobrowse.isInSession();
      this.nativeInSession = !!status?.inSession;
    } catch {
      this.nativeInSession = false;
    }
    if (this.nativeInSession) {
      this.nativeStartPending = false;
      this.loggingService.logWarning('Already in a native GLANCE co-browsing session');
      return {
        success: false,
        error: 'A screen sharing session is already active.',
        errorCode: 'sessionAlreadyActive',
      };
    }

    try {
      await GlanceCobrowse.startSession();
      this.nativeInSession = true;
      this.analyticsService.trackCoBrowseRequested();
      this.dynatraceService.trackButtonClick('Share My Screen', 'co-browse');
      this.loggingService.logInfo('GLANCE native session started (DefaultUI handles overlay)');
      return { success: true };
    } catch (err) {
      this.nativeInSession = false;
      this.loggingService.logException(err, 'Failed to start native GLANCE session:');
      return { success: false, error: 'Failed to start screen sharing. Please try again.', errorCode: 'startFailed' };
    } finally {
      this.nativeStartPending = false;
    }
  }

  async endNativeSession(): Promise<{ success: boolean; error?: string }> {
    if (!this.nativeInSession) {
      try {
        const status = await GlanceCobrowse.isInSession();
        this.nativeInSession = !!status?.inSession;
      } catch {
        this.nativeInSession = false;
      }
      if (!this.nativeInSession) {
        return { success: false, error: 'No active native session to end.' };
      }
    }

    try {
      await GlanceCobrowse.endSession();
      this.nativeInSession = false;
      return { success: true };
    } catch (err) {
      this.loggingService.logException(err, 'Failed to end native GLANCE session:');
      return { success: false, error: 'Failed to end screen sharing session.' };
    }
  }

  private registerNativeListeners(): void {
    if (this.nativeListenersRegistered) {
      return;
    }
    this.nativeListenersRegistered = true;

    GlanceCobrowse.addListener('sessionEnded', () => {
      this.ngZone.run(() => {
        this.nativeInSession = false;
        this.loggingService.logInfo('GLANCE native session ended');
      });
    });

    GlanceCobrowse.addListener('error', (event) => {
      this.ngZone.run(() => {
        this.nativeInSession = false;
        this.loggingService.logWarning(`GLANCE native SDK error: ${event.error}`);
      });
    });
  }

  // ---------------------------------------------------------------------------
  // Web SDK helpers
  // ---------------------------------------------------------------------------

  private waitForSdkReady(timeoutMs: number): Promise<boolean> {
    return new Promise((resolve) => {
      if (this.isLoaded()) {
        resolve(true);
        return;
      }

      let elapsed = 0;
      this.ngZone.runOutsideAngular(() => {
        const check = setInterval(() => {
          elapsed += GlanceService.SDK_POLL_INTERVAL_MS;
          if (this.isLoaded()) {
            clearInterval(check);
            resolve(true);
          } else if (elapsed >= timeoutMs) {
            clearInterval(check);
            resolve(false);
          }
        }, GlanceService.SDK_POLL_INTERVAL_MS);
      });
    });
  }

  private loadScript(): Promise<void> {
    if (this.scriptLoadPromise) {
      return this.scriptLoadPromise;
    }

    this.scriptLoadPromise = new Promise<void>((resolve, reject) => {
      const existing = document.getElementById('glance-cobrowse');
      if (existing) {
        existing.remove();
      }

      const script = document.createElement('script');
      script.id = 'glance-cobrowse';
      script.charset = 'UTF-8';
      script.type = 'text/javascript';
      script.src = this.glanceScriptUrl;
      script.setAttribute('data-groupid', this.glanceGroupId);
      script.setAttribute('data-site', this.glanceSite);
      script.setAttribute('data-ws', this.glanceWs);
      script.setAttribute('data-cookietype', 'normal');

      script.onload = () => {
        this.scriptLoadPromise = null;
        resolve();
      };
      script.onerror = () => {
        this.scriptLoadPromise = null;
        reject(new Error('Failed to load GLANCE cobrowse script from CDN'));
      };

      document.body.appendChild(script);
    });

    return this.scriptLoadPromise;
  }
}
